import {
  ProtoSpriteSheetThree,
  ProtoSpriteSheetThreeLoader,
  ProtoSpriteThree,
  ProtoSpriteThreeExtended,
  SliceRegion
} from "protosprite-three";

import { LoadableResource } from "./LoadableResource";

export type TLayerAndAnimationsMap<
  T extends Record<
    string | number,
    {
      layers?: string;
      tags?: string;
    }
  >
> = {
  [K in keyof T]: K extends string | number
    ? T[K] extends {
        layers?: infer TL extends string;
        tags?: infer TT extends string;
      }
      ? {
          layers?: TL;
          tags: TT;
        }
      : never
    : never;
};

type TDefaultLayerAndAnimationsMap = TLayerAndAnimationsMap<{
  0: {
    layers: string;
    tags: string;
  };
}>;

type Default0<T> = T extends string | number ? T : 0;

export type ProtoSpriteSheetThreeWithTypes<
  T extends TDefaultLayerAndAnimationsMap = TDefaultLayerAndAnimationsMap
> = Omit<ProtoSpriteSheetThree, "getsprite" | "getSpriteExtended"> & {
  getSprite(
    indexOrName?: keyof T
  ): ProtoSpriteThree<
    (T[Default0<typeof indexOrName>] & { layers?: string })["layers"],
    (T[Default0<typeof indexOrName>] & { tags?: string })["tags"]
  >;
  getSpriteExtended<
    T extends TDefaultLayerAndAnimationsMap = TDefaultLayerAndAnimationsMap,
    TRegions extends string = "main"
  >(
    indexOrName?: keyof T,
    regions?: SliceRegion<TRegions>[]
  ): ProtoSpriteThreeExtended<
    (T[Default0<typeof indexOrName>] & { layers?: string })["layers"],
    (T[Default0<typeof indexOrName>] & { tags?: string })["tags"],
    TRegions
  >;
};

export class LoadableProtospriteResource<
  TL extends string = string,
  TT extends string = string
> extends LoadableResource<
  ProtoSpriteSheetThreeWithTypes<{
    0: {
      layers: TL;
      tags: TT;
    };
  }>
> {
  // Potential kludge here. I don't care.
  static prsSheetLoader = new ProtoSpriteSheetThreeLoader();
  private resourceUrl: string;

  constructor(prsUrl: string, sheetIndexOrName?: string | number) {
    super();
    this.resourceUrl = prsUrl;
  }

  async getResourcePromise(): Promise<
    ProtoSpriteSheetThreeWithTypes<{
      0: {
        layers: TL;
        tags: TT;
      };
    }>
  > {
    await this.load();
    return this.getResource();
  }
  async load(): Promise<void> {
    await super.internalResourceLoadSequenceFromFunction(() => {
      return LoadableProtospriteResource.prsSheetLoader.loadAsync(
        this.resourceUrl
      ) as Promise<
        ProtoSpriteSheetThreeWithTypes<{
          0: {
            layers: TL;
            tags: TT;
          };
        }>
      >;
    });
  }
}
